import { ReactElement, ReactNode, useRef, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/misc/Utils'
import ArrowTopRightOnSquare from '@icons/arrow-top-right-on-square'

export interface LinkProps {
  id?: string
  label: string | ReactElement
  to?: string
  isSeparated?: boolean
  onClick?: () => void
  cy?: string
  icon?: ReactElement
  external?: boolean
}

interface DropdownMenuProps {
  links: LinkProps[]
  children: ReactNode
  className?: string
  alignment?: 'left' | 'right'
  width?: number | string
  id?: string
  fullWidth?: boolean
}

const DropdownMenu = (props: DropdownMenuProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [toggled, setToggle] = useState(false)
  const [menuPosition, setMenuPosition] = useState<{
    top: number
    left: number
    width: number
    maxHeight: number
    flipped: boolean
  } | null>(null)
  const [shouldRender, setShouldRender] = useState(toggled)
  const [animate, setAnimate] = useState(false)

  const {
    children,
    links,
    className = 'flex items-center',
    alignment = 'right',
    fullWidth = false,
  } = props

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !triggerRef.current?.contains(event.target as Node) &&
        !menuRef.current?.contains(event.target as Node)
      ) {
        setToggle(false)
      }
    }

    if (toggled) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [toggled])

  useEffect(() => {
    if (!toggled || !triggerRef.current) return

    const GAP = 8
    const MIN_SPACE = 180

    const position = () => {
      const trigger = triggerRef.current
      if (trigger == null) return

      const rect = trigger.getBoundingClientRect()
      const menuWidth =
        typeof props.width === 'number' ? props.width : rect.width

      // Keep the menu inside the viewport horizontally.
      const rawLeft = alignment === 'left' ? rect.left : rect.right - menuWidth
      const clampedLeft = Math.min(
        Math.max(GAP, rawLeft),
        Math.max(GAP, window.innerWidth - menuWidth - GAP)
      )

      // Open downwards when there is room, otherwise flip above the trigger.
      const spaceBelow = window.innerHeight - rect.bottom - GAP
      const spaceAbove = rect.top - GAP
      const flipped = spaceBelow < MIN_SPACE && spaceAbove > spaceBelow

      setMenuPosition({
        top: (flipped ? rect.top - GAP : rect.bottom + GAP) + window.scrollY,
        left: clampedLeft + window.scrollX,
        width: rect.width,
        maxHeight: Math.max(MIN_SPACE, flipped ? spaceAbove : spaceBelow),
        flipped,
      })
    }

    position()
    window.addEventListener('resize', position)
    window.addEventListener('scroll', position, true)
    return () => {
      window.removeEventListener('resize', position)
      window.removeEventListener('scroll', position, true)
    }
  }, [toggled, alignment, props.width])

  useEffect(() => {
    if (toggled) {
      setShouldRender(true)
    } else {
      const timeout = setTimeout(() => {
        setShouldRender(false)
        setMenuPosition(null)
      }, 200)
      return () => clearTimeout(timeout)
    }
  }, [toggled])

  useEffect(() => {
    if (toggled) {
      const timer = setTimeout(() => {
        setAnimate(true)
      }, 10)
      return () => clearTimeout(timer)
    }
    setAnimate(false)
  }, [toggled])

  return (
    <div className={className}>
      <div className={cn('relative', fullWidth ? 'w-full' : '')}>
        <button
          ref={triggerRef}
          type="button"
          className={cn('flex rounded-lg', fullWidth ? 'w-full' : '')}
          id={props.id}
          data-cy={props.id}
          aria-expanded={toggled}
          aria-haspopup="true"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            setToggle(!toggled)
          }}
        >
          <span className="sr-only">Open menu</span>
          {children}
        </button>

        {shouldRender &&
          menuPosition &&
          createPortal(
            <div
              ref={menuRef}
              className={cn(
                'fixed transition-opacity duration-150 ease-out',
                'rounded-xl shadow-popover p-1 bg-surface focus:outline-hidden z-250 text-left',
                animate ? 'opacity-100' : 'opacity-0 pointer-events-none'
              )}
              style={{
                position: 'absolute',
                top: menuPosition.top,
                left: menuPosition.left,
                width: props.width || menuPosition.width,
                maxHeight: menuPosition.maxHeight,
                overflowY: 'auto',
                transform: menuPosition.flipped
                  ? 'translateY(-100%)'
                  : undefined,
                zIndex: 250,
              }}
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="user-menu-button"
              tabIndex={-1}
            >
              {links?.map((link, index) => {
                const itemProps = {
                  className: cn(
                    'block text-sm text-text-base',
                    link.isSeparated && 'mt-1 pt-1 border-t border-hairline'
                  ),
                  role: 'menuitem',
                  tabIndex: -1,
                  cy: link.cy || `menu-${link.id}`,
                }

                const label = (
                  <span className="block px-2.5 py-1.5 text-[14px] text-neutral-70 hover:bg-accent-60 hover:text-white transition-colors cursor-pointer flex items-center space-x-2 justify-between group rounded-md">
                    <div className="flex items-center space-x-2">
                      {link.icon && (
                        <div className="size-4 text-neutral-50 group-hover:text-white">
                          {link.icon}
                        </div>
                      )}
                      <span className="group-hover:text-white">
                        {link.label}
                      </span>
                    </div>
                    {link.external && (
                      <div className="opacity-50 size-3 ml-auto group-hover:opacity-70">
                        <ArrowTopRightOnSquare className="h-full w-full" />
                      </div>
                    )}
                  </span>
                )

                return link.to ? (
                  <Link
                    {...itemProps}
                    key={index}
                    to={link.to}
                    onClick={() => setToggle(false)}
                    data-cy={itemProps.cy}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                  >
                    {label}
                  </Link>
                ) : (
                  <div
                    {...itemProps}
                    key={index}
                    data-cy={itemProps.cy}
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      setToggle(false)
                      link.onClick?.()
                    }}
                  >
                    {label}
                  </div>
                )
              })}
            </div>,
            document.body
          )}
      </div>
    </div>
  )
}

export default DropdownMenu
